import type { LegacyOffer, LegacyOffersPayload } from '../model/order-schema';
import {
  createOfferCatalog,
  statsFromCounts,
  toLatLong,
  type OfferReason,
  type OfferRecord,
  type OfferTone,
  type OffersView,
} from './offers';

/**
 * The "legacy" offers source: cpanel `LogsInfo`'s own API (the main gateway's
 * `admin/orders/{id}/offers`), with its reason table (`assets/data/offer-reasons`),
 * its server-side legend tally, and its rules — an offer is accepted when it has
 * `accepted_at`, and reached the courier when it has `offered_at`.
 */

const reason = (id: string, number: number, tone: OfferTone): OfferReason => ({
  id,
  number,
  tone,
  labelKey: `legacyReason.${id}`,
});

/**
 * cpanel's reason table. Tones are its MUI palette entries mapped onto the
 * `--offer-*` tokens (lightBlue.500 → sky, grey.500 → neutral, teal.700 → teal,
 * blue.600/.500 → blue/blue-soft, yellow.500 → amber, orange.400/.500/.600 →
 * orange-soft/orange/orange-deep, red.400 → red-soft, purple.400/.600 →
 * violet-soft/violet). The original lists 16–20 twice; `find` only ever
 * reached the first copies, which are all that is kept here.
 */
export const LEGACY_OFFER_REASONS: readonly OfferReason[] = [
  reason('ReasonBalanceInt', 1, 'sky'),
  reason('ReasonVolumeInt', 2, 'orange'),
  reason('ReasonNotWorkingInt', 3, 'neutral'),
  reason('ReasonWrongEquipmentInt', 4, 'pink'),
  reason('ReasonRangeInt', 5, 'teal'),
  reason('ReasonHasActiveOrderInt', 6, 'blue'),
  reason('ReasonMissedInt', 7, 'blue-soft'),
  reason('ReasonNetworkInt', 8, 'amber'),
  reason('ReasonStartPointNotReachedInt', 9, 'amber'),
  reason('ReasonMaxOrdersReachedInt', 10, 'amber'),
  reason('ReasonDirection', 11, 'amber'),
  reason('ReasonMultizakazNotReserved', 13, 'orange-soft'),
  reason('ReasonFirstOrderLateInt', 14, 'orange-soft'),
  reason('ReasonOfferRejectionLimit', 15, 'orange-soft'),
  reason('ReasonMultizakazInvalidRoute', 16, 'red-soft'),
  reason('ReasonMultizakazPickupCorridor', 17, 'orange-soft'),
  reason('ReasonMultizakazDestCorridor', 18, 'orange-deep'),
  reason('ReasonMultizakazDestAngle', 19, 'violet-soft'),
  reason('ReasonMultizakazDestBehindPickup', 20, 'violet'),
];

export const LEGACY_OFFER_CATALOG = createOfferCatalog('legacy', LEGACY_OFFER_REASONS);

export function legacyOfferRecords(offers: readonly LegacyOffer[]): OfferRecord[] {
  return offers.map((raw) => ({
    mode: 'legacy',
    raw,
    id: raw.id ?? null,
    courierId: raw.courier_courier_id ?? null,
    coords: toLatLong(raw.courier_location),
    roadMeters: raw.dead_head_distance ?? null,
    facts: {
      reason: raw.reason ?? null,
      accepted: Boolean(raw.accepted_at),
      reached: Boolean(raw.offered_at),
    },
    sortAt: raw.offered_at ?? null,
  }));
}

export function legacyOffersView(payload: LegacyOffersPayload): OffersView {
  return {
    mode: 'legacy',
    records: legacyOfferRecords(payload.offers),
    stats: statsFromCounts(payload.statistics, LEGACY_OFFER_CATALOG),
  };
}
